import { useState, useEffect, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Check, X } from 'lucide-react'

interface TooltipPosition {
  top: number
  left: number
}

interface AnnotationTooltipProps {
  containerRef: React.RefObject<HTMLDivElement | null>
  onAccept: (element: HTMLElement, type: 'addition' | 'deletion' | 'note') => void
  onReject: (element: HTMLElement, type: 'addition' | 'deletion' | 'note') => void
  disabled?: boolean
}

/**
 * Hover tooltip for individual annotation accept/reject.
 * Attaches to a container and shows accept/reject buttons when hovering over annotations.
 */
export function AnnotationTooltip({
  containerRef,
  onAccept,
  onReject,
  disabled = false,
}: AnnotationTooltipProps) {
  const [visible, setVisible] = useState(false)
  const [position, setPosition] = useState<TooltipPosition>({ top: 0, left: 0 })
  const [activeElement, setActiveElement] = useState<HTMLElement | null>(null)
  const [annotationType, setAnnotationType] = useState<'addition' | 'deletion' | 'note'>('addition')
  const tooltipRef = useRef<HTMLDivElement>(null)
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearHideTimeout = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }
  }, [])

  const scheduleHide = useCallback(() => {
    clearHideTimeout()
    hideTimeoutRef.current = setTimeout(() => {
      setVisible(false)
      setActiveElement(null)
    }, 150) // Small delay to allow moving to tooltip
  }, [clearHideTimeout])

  const handleMouseEnter = useCallback((e: MouseEvent) => {
    if (disabled) return

    const target = e.target as HTMLElement

    // Check if hovering over an annotation
    const isAddition = target.classList.contains('annotation-addition')
    const isDeletion = target.classList.contains('annotation-deletion')
    const isNote = target.classList.contains('annotation-note')

    if (!isAddition && !isDeletion && !isNote) return

    clearHideTimeout()

    // Determine annotation type
    if (isAddition) setAnnotationType('addition')
    else if (isDeletion) setAnnotationType('deletion')
    else if (isNote) setAnnotationType('note')

    // Calculate position (above the annotation)
    const rect = target.getBoundingClientRect()
    setPosition({
      top: rect.top - 36, // Above the element
      left: rect.left + rect.width / 2, // Centered
    })

    setActiveElement(target)
    setVisible(true)
  }, [disabled, clearHideTimeout])

  const handleMouseLeave = useCallback((e: MouseEvent) => {
    const relatedTarget = e.relatedTarget as HTMLElement

    // Check if moving to tooltip
    if (tooltipRef.current?.contains(relatedTarget)) {
      return
    }

    // Check if moving to another annotation
    if (relatedTarget?.classList?.contains('annotation-addition') ||
        relatedTarget?.classList?.contains('annotation-deletion') ||
        relatedTarget?.classList?.contains('annotation-note')) {
      return
    }

    scheduleHide()
  }, [scheduleHide])

  const handleTooltipMouseEnter = useCallback(() => {
    clearHideTimeout()
  }, [clearHideTimeout])

  const handleTooltipMouseLeave = useCallback(() => {
    scheduleHide()
  }, [scheduleHide])

  const handleAccept = useCallback(() => {
    if (activeElement) {
      onAccept(activeElement, annotationType)
      setVisible(false)
      setActiveElement(null)
    }
  }, [activeElement, annotationType, onAccept])

  const handleReject = useCallback(() => {
    if (activeElement) {
      onReject(activeElement, annotationType)
      setVisible(false)
      setActiveElement(null)
    }
  }, [activeElement, annotationType, onReject])

  // Attach event listeners to container
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    // Use event delegation on the container
    container.addEventListener('mouseover', handleMouseEnter)
    container.addEventListener('mouseout', handleMouseLeave)

    return () => {
      container.removeEventListener('mouseover', handleMouseEnter)
      container.removeEventListener('mouseout', handleMouseLeave)
      clearHideTimeout()
    }
  }, [containerRef, handleMouseEnter, handleMouseLeave, clearHideTimeout])

  // Hide tooltip on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (visible) {
        setVisible(false)
        setActiveElement(null)
      }
    }

    window.addEventListener('scroll', handleScroll, true)
    return () => window.removeEventListener('scroll', handleScroll, true)
  }, [visible])

  if (!visible || disabled) return null

  // Get label based on annotation type
  const getAcceptLabel = () => {
    switch (annotationType) {
      case 'addition': return 'Keep'
      case 'deletion': return 'Remove'
      case 'note': return 'Remove'
    }
  }

  const getRejectLabel = () => {
    switch (annotationType) {
      case 'addition': return 'Discard'
      case 'deletion': return 'Keep'
      case 'note': return 'Keep'
    }
  }

  return createPortal(
    <div
      ref={tooltipRef}
      className="annotation-tooltip"
      style={{
        position: 'fixed',
        top: position.top,
        left: position.left,
        transform: 'translateX(-50%)',
        zIndex: 9999,
      }}
      onMouseEnter={handleTooltipMouseEnter}
      onMouseLeave={handleTooltipMouseLeave}
    >
      <div className="flex items-center gap-1 bg-popover border border-border rounded-md shadow-lg p-1">
        <button
          onClick={handleAccept}
          className={`flex items-center gap-1 px-2 py-1 text-xs font-medium rounded transition-colors ${
            annotationType === 'addition'
              ? 'text-green-600 hover:bg-green-500/10'
              : 'text-red-600 hover:bg-red-500/10'
          }`}
          title={getAcceptLabel()}
        >
          <Check className="w-3 h-3" />
          <span>{getAcceptLabel()}</span>
        </button>
        <div className="w-px h-4 bg-border" />
        <button
          onClick={handleReject}
          className={`flex items-center gap-1 px-2 py-1 text-xs font-medium rounded transition-colors ${
            annotationType === 'addition'
              ? 'text-muted-foreground hover:bg-muted'
              : 'text-muted-foreground hover:bg-muted'
          }`}
          title={getRejectLabel()}
        >
          <X className="w-3 h-3" />
          <span>{getRejectLabel()}</span>
        </button>
      </div>
      {/* Arrow pointing down to the annotation */}
      <div
        className="absolute left-1/2 -translate-x-1/2 -bottom-1 w-2 h-2 bg-popover border-r border-b border-border rotate-45"
      />
    </div>,
    document.body
  )
}
